import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { Lock, Eye, EyeOff } from 'lucide-react';
import Modal from '../../atoms/modal/view';
import ButtonBasic from '../../atoms/buttonBasic/view';
import { getPasswordStrength } from '../../../utils/validate';
import { signInWithEmailAndPassword } from '../../../usecases/auth-usecase';
import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import './style.css';

/**
 * Reusable Password Change Modal
 * Handles re-authentication and password update flow.
 * 
 * @param {string} id - Unique ID for the modal
 * @param {string} userEmail - Current logged-in user's email for re-auth
 * @param {string} targetName - Name of the person whose password is being changed
 * @param {function} onUpdatePassword - Async function(newPassword) to update the password
 */
const PasswordChangeModal = ({ id, userEmail, targetName = 'tu cuenta', onUpdatePassword }) => {
    const [passwordFlowStep, setPasswordFlowStep] = useState('reauth');
    const [ownerPassword, setOwnerPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Password visibility states
    const [showOwnerPassword, setShowOwnerPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    useEffect(() => {
        // Reset state when modal opens
        const resetState = () => {
            setPasswordFlowStep('reauth');
            setOwnerPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setShowOwnerPassword(false);
            setShowNewPassword(false);
            setShowConfirmNewPassword(false);
            setActionLoading(false);
        };

        const unbindOpen = listener('modal' + id, resetState);
        return () => {
            if (unbindOpen) unbindOpen();
        };
    }, [id]);

    const handleReAuthSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!ownerPassword) return;

        setActionLoading(true);
        try {
            await signInWithEmailAndPassword(userEmail, ownerPassword);
            setPasswordFlowStep('password');
        } catch (err) {
            dispatchCustomEvent('popup', { message: '❌ Contraseña de administrador incorrecta', type: 'error' });
        } finally {
            setActionLoading(false);
            dispatchCustomEvent('loading-btn-reauth-' + id);
        }
    };

    const handlePasswordUpdateSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!newPassword || newPassword !== confirmNewPassword) {
            dispatchCustomEvent('popup', { message: '❌ Las contraseñas no coinciden', type: 'error' });
            return;
        }

        if (newPassword.length < 6) {
            dispatchCustomEvent('popup', { message: '❌ La contraseña debe tener al menos 6 caracteres', type: 'error' });
            return;
        }

        setActionLoading(true);
        try {
            await onUpdatePassword(newPassword);
            // The parent component should handle the success message and closing the modal
            // if it hasn't already by the time this promise resolves.
        } catch (err) {
            dispatchCustomEvent('popup', { message: `❌ Error: ${err.message || 'No se pudo actualizar la contraseña'}`, type: 'error' });
        } finally {
            setActionLoading(false);
            dispatchCustomEvent('loading-btn-update-pass-' + id);
        }
    };

    return (
        <Modal id={id} className="password-modal-redesign">
            <div className="redesign-modal-container">
                {passwordFlowStep === 'reauth' ? (
                    <>
                        <div className="redesign-modal-header">
                            <h3 className="redesign-modal-title">Re-autenticación</h3>
                            <p className="redesign-modal-desc">
                                Por seguridad, ingresa tu <b>{targetName === 'tu cuenta' ? 'contraseña actual' : 'contraseña de administrador'}</b> para modificar {targetName === 'tu cuenta' ? 'tu cuenta' : <>la cuenta de <b>{targetName}</b></>}.
                            </p>
                        </div>

                        <Form className="redesign-modal-body" onSubmit={handleReAuthSubmit}>
                            <Form.Group>
                                <Form.Label className="redesign-label">{targetName === 'tu cuenta' ? 'Tu Contraseña Actual' : 'Tu Contraseña de Administrador'}</Form.Label>
                                <div className="pcm-input-container">
                                    <Lock className="redesign-input-icon" size={20} />
                                    <Form.Control
                                        type={showOwnerPassword ? "text" : "password"}
                                        placeholder={targetName === 'tu cuenta' ? 'Ingresa tu clave actual' : 'Ingresa tu clave de administrador'}
                                        className="redesign-input with-toggle"
                                        value={ownerPassword}
                                        onChange={(e) => setOwnerPassword(e.target.value)}
                                        required
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="pcm-toggle-btn"
                                        onClick={() => setShowOwnerPassword(!showOwnerPassword)}
                                    >
                                        {showOwnerPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </Form.Group>

                            <ButtonBasic
                                id={"reauth-" + id}
                                showLoading={true}
                                title="Verificar Identidad"
                                onClick={handleReAuthSubmit}
                                className="redesign-action-btn"
                                disabled={!ownerPassword || actionLoading}
                            />
                        </Form>
                    </>
                ) : (
                    <>
                        <div className="redesign-modal-header">
                            <h3 className="redesign-modal-title">Nueva Contraseña</h3>
                            <p className="redesign-modal-desc">
                                Establece una nueva contraseña de acceso {targetName === 'tu cuenta' ? '' : <>para <b>{targetName}</b></>}.
                            </p>
                        </div>

                        <Form className="redesign-modal-body" onSubmit={handlePasswordUpdateSubmit}>
                            <Form.Group>
                                <Form.Label className="redesign-label">Nueva Contraseña</Form.Label>
                                <div className="pcm-input-container">
                                    <Lock className="redesign-input-icon" size={20} />
                                    <Form.Control
                                        type={showNewPassword ? "text" : "password"}
                                        placeholder="Mínimo 6 caracteres"
                                        className="redesign-input with-toggle"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="pcm-toggle-btn"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                    >
                                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>

                                {newPassword && (
                                    <div className="password-strength-container">
                                        <div className="password-strength-bar-wrapper">
                                            <div className={`password-strength-bar ${getPasswordStrength(newPassword).class}`}></div>
                                        </div>
                                        <span className={`password-strength-text ${getPasswordStrength(newPassword).class}`}>
                                            Seguridad: {getPasswordStrength(newPassword).label}
                                        </span>
                                    </div>
                                )}
                            </Form.Group>

                            <Form.Group>
                                <Form.Label className="redesign-label">Confirmar Contraseña</Form.Label>
                                <div className="pcm-input-container">
                                    <Lock className="redesign-input-icon" size={20} />
                                    <Form.Control
                                        type={showConfirmNewPassword ? "text" : "password"}
                                        placeholder="Repite la contraseña"
                                        className="redesign-input with-toggle"
                                        value={confirmNewPassword}
                                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="pcm-toggle-btn"
                                        onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                    >
                                        {showConfirmNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </Form.Group>

                            <ButtonBasic
                                id={"update-pass-" + id}
                                showLoading={true}
                                title="Actualizar Contraseña"
                                onClick={handlePasswordUpdateSubmit}
                                className="redesign-action-btn"
                                disabled={!newPassword || !confirmNewPassword || newPassword !== confirmNewPassword || newPassword.length < 6 || actionLoading}
                            />
                        </Form>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default PasswordChangeModal;
